import { useMemo } from 'react'
import { keepPreviousData } from '@tanstack/react-query'

import useQuery from '@shared/hooks/src/useQuery'
import { useMe } from '@shared/providers/src/MeProvider'
import API from '@shared/services/src/API'
import { GenericTodoType, QK, TodoType, TodoTypeOrder } from '@shared/utils'

import { useProfileMissingErrors } from '@pages/Account/Account.hooks'

// Get Todo by provided type from the cache
export function useTodoByType(type) {
  const { id } = useMe()

  // Works only with the cache
  return useQuery({
    queryKey: QK.patients.id(id).todos.list(),
    queryFn: () => API.patients.id(id).todos.list(),
    enabled: false,
    select: (todos) => todos.find((todo) => todo.type === type),
  })
}

export function useTodos() {
  const me = useMe()

  const { data: todosData, isPending: areTodosPending } = useQuery({
    queryKey: QK.patients.id(me.id).todos.list(),
    queryFn: () => API.patients.id(me.id).todos.list(),
    // It is set to 0 so that the todos are always refetched when the user navigates to the home page.
    staleTime: 0,
    placeholderData: keepPreviousData,
  })

  const { data: profileMissingErrorsData, isPending: isProfileMissingErrorsPending } = useProfileMissingErrors()

  const todos = useMemo(() => {
    if (!todosData) return todosData

    const todos = Array.from(todosData)
    const isProfileIncomplete = Object.values(profileMissingErrorsData).some((value) => value > 0)

    if (!me.photoId) todos.push({ type: TodoType.PhotoId, precedence: TodoTypeOrder[TodoType.PhotoId] })
    if (isProfileIncomplete) todos.push({ type: TodoType.ProfileIncomplete, precedence: TodoTypeOrder[TodoType.ProfileIncomplete] })

    return todos.sort((x, y) => x.precedence - y.precedence || new Date(x.created_at) - new Date(y.created_at))
  }, [me.photoId, profileMissingErrorsData, todosData])

  return {
    data: todos,
    isPending: areTodosPending || isProfileMissingErrorsPending,
  }
}

const APPOINTMENT_TODO_TYPES = [TodoType.InitialAppointment, TodoType.RescheduleAppointment]

const LAB_TODO_TYPES = [TodoType.LabFlow, TodoType.HivSelfTest]

// UNREAD is excluded on purpose: the Chats tab already badges unread conversations.
const OTHER_TODO_TYPES = [
  TodoType.RestartCare,
  TodoType.PaymentFailure,
  TodoType.PhotoId,
  TodoType.ProfileIncomplete,
  TodoType.Insurance,
  TodoType.GileadApplication,
  TodoType.ROIRequest,
  TodoType.Assessment,
]

const isNewDocument = (todo) => todo.type === TodoType.Generic && todo.todo?.todoType === GenericTodoType.NewDocument

/**
 * The to-do list, split by the Home block that draws each one.
 *
 * @returns {{appointments: Array, labs: Array, other: Array, count: number, isPending: boolean}} The to-dos per block.
 */
export function useHomeTodos() {
  const { data: todos = [], isPending } = useTodos()

  return useMemo(() => {
    const appointments = todos.filter((todo) => APPOINTMENT_TODO_TYPES.includes(todo.type))
    const labs = todos.filter((todo) => LAB_TODO_TYPES.includes(todo.type))
    const other = todos.filter((todo) => OTHER_TODO_TYPES.includes(todo.type) || isNewDocument(todo))

    return { appointments, labs, other, count: appointments.length + labs.length + other.length, isPending }
  }, [todos, isPending])
}
